import React from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import { Dashboard, PageLayout } from '@components/common'
import { SettingsTabs } from '@components/common/SettingsTabs'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { PermissionGuardedRoute } from '@components/utils/DashboardPermissions'
import { SECTION_NAMES } from '@constants/sectionNames'
import { HeadContent } from '@fourthwall/components'
import {
  CogFilledIcon,
  CogIcon,
  ConnectedSitesFilledIcon,
  ConnectedSitesIcon,
  EmailNotificationsFilledIcon,
  EmailNotificationsIcon,
  SecurityFilledIcon,
  SecurityIcon,
} from '@fourthwall/icons'
import { isWebViewUserAgent } from '@fourthwall/webview-bridge/utils'
import { routing } from '@utils/routing'

import { CONNECTED_SITES_TITLE, ConnectedSites } from './pages/ConnectedSites'
import { EmailNotifications } from './pages/EmailNotifications'
import { GENERAL_VIEW_TITLE, General } from './pages/General'
import { SecurityContainer } from './pages/Security'

const createPath = (path: string) => {
  return path.replace(routing.account.self, '')
}

const title = 'Account settings'

export const AccountView = () => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { pathname } = useLocation()

  const routes = [
    {
      name: GENERAL_VIEW_TITLE,
      path: routing.account.self,
      icon: CogIcon,
      activeIcon: CogFilledIcon,
      element: <PermissionGuardedRoute permission="accountSettings" element={<General />} />,
      enabled: true,
    },
    {
      name: SECTION_NAMES.accountSecurity,
      path: routing.account.security,
      icon: SecurityIcon,
      activeIcon: SecurityFilledIcon,
      element: <SecurityContainer />,
      enabled: dashboardFeatureFlags.accountSettingsSecurity.enabled,
    },
    {
      name: 'Email notifications',
      path: routing.account.emailNotifications,
      icon: EmailNotificationsIcon,
      activeIcon: EmailNotificationsFilledIcon,
      element: (
        <PermissionGuardedRoute permission="accountSettings" element={<EmailNotifications />} />
      ),
      enabled: true,
    },
    {
      name: CONNECTED_SITES_TITLE,
      path: routing.account.connectedSites,
      icon: ConnectedSitesIcon,
      activeIcon: ConnectedSitesFilledIcon,
      element: <PermissionGuardedRoute permission="accountSettings" element={<ConnectedSites />} />,
      enabled: true,
    },
  ].filter((route) => route.enabled)

  const tabs = routes.map(({ name, path, icon, activeIcon }) => ({
    name,
    path,
    icon,
    activeIcon,
  }))

  const getSectionName = () => {
    if (!isWebViewUserAgent(navigator.userAgent)) {
      return title
    }

    const normalizedPath = createPath(pathname)

    const currentTab = routes.find((route) => {
      const routePath = createPath(route.path)

      return normalizedPath === routePath || normalizedPath.startsWith(`${routePath}/`)
    })

    return currentTab?.name || title
  }

  return (
    <>
      <HeadContent title="Account settings" />
      <PageLayout size="regular">
        <Dashboard.Section name={getSectionName()}>
          <SettingsTabs
            tabs={tabs}
            element={
              <Routes>
                {routes.map(({ name, path, element }) => (
                  <Route key={name} path={createPath(path)} element={element} />
                ))}
              </Routes>
            }
          />
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
